import css from '../../../../../controls/Form/FormWrapper/FormWrapper.module.scss';
import { FilterOptionsState } from '@mui/material';
import { IAppointment } from '../../../../../API/GoMoto';
import { FormProvider } from '../../../../../context/Form/form';
import { useLocalization } from '../../../../../context/Localization/localization';
import { AppointmentCard } from '../../../../../controls/AppointmentCard/AppointmentCard';
import { GMAutocomplete } from '../../../../../controls/Autocomplete';
import { FilterButtons, FilterButton } from '../../../../../controls/SearchGrid/FilterButtons/FilterButtons';
import { FilterRow } from '../../../../../controls/SearchGrid/FilterRow/FilterRow';
import { GMSelect } from '../../../../../controls/Select';
import { getFullDisplayName } from '../../../../../helpers/Appointment';
import { nameof } from '../../../../../helpers/nameof';
import { DatePicker } from '../../../../../controls/DatePicker/DatePicker';
import { AppointmentType, DateRangeKey } from '../../../../../API/Enums';
import { GoMotoComponent } from '../../../../../API/GoMoto.types';
import { AppointmentColors, FilterProps } from './Filters.types';
import { useDateRanges } from '../../../../../helpers/Hooks/DateRanges/DateRanges';
import { AdminFiltersDispatchTypes, FilterValues } from '../../../../../context/AdminFilters/AdminFilters.types';
import { useKioskState } from '../../../../../context/Kiosk/kiosk';
import { useAdminState } from '../../../../../context/Admin/admin';
import { useAdminFiltersDispatch, useAdminFiltersState } from '../../../../../context/AdminFilters/AdminFilters';
import { memo, useState, useEffect } from 'react';

export const Filters: GoMotoComponent<FilterProps> = memo(({
    includeFilters,
    tabType,
}) => {
    const Localization = useLocalization();
    const dateRanges = useDateRanges();
    const { filterValues, results } = useAdminFiltersState();
    const dispatch = useAdminFiltersDispatch();
    const { key: kioskKey, dealer: { key: dealerKey } } = useKioskState();
    const { LoginSessionID } = useAdminState();

    const [filters, setFilters] = useState<FilterValues | undefined>(filterValues);

    useEffect(() => {
        dispatch({
            type: AdminFiltersDispatchTypes.SetFilters,
            payload: {
                appointmentFilters: {
                    kioskKey: kioskKey,
                    dealerKey: dealerKey,
                    keySlotEmpty: tabType === 'check-ins',
                    sessionId: LoginSessionID,
                },
                filterValues: filters,
            },
        });
    }, [LoginSessionID, dealerKey, dispatch, filterValues?.type, filters, kioskKey, tabType]);

    const getOptionLabel = (option: string | IAppointment): string => {
        if (option && Object.keys(option).length !== 0 && typeof option !== 'string') {
            return getFullDisplayName(option);
        }
        else {
            return '';
        }
    };

    const filterOptions = (options: IAppointment[], state: FilterOptionsState<IAppointment>): IAppointment[] => {
        if (state.inputValue === '') {
            return [];
        }
        const filteredOptions = options.filter((option: IAppointment) =>
            option.firstName?.toLocaleLowerCase()
                .includes(state.inputValue?.toLocaleLowerCase()) ||
            option.lastName?.toLocaleLowerCase()
                .includes(state.inputValue?.toLocaleLowerCase()) ||
            option.businessName?.toLocaleLowerCase()
                .includes(state.inputValue?.toLocaleLowerCase()),
        );
        if (filteredOptions.length > 0) {
            return filteredOptions;
        }
        else {
            return [];
        }
    };

    const renderOption = (option: IAppointment) => (
        <AppointmentCard
            key={option.key}
            appointment={option}
            color={AppointmentColors[AppointmentType.none]}
        />
    );

    const onOptionSelected = (option: string | IAppointment | null) => {
        if (typeof option !== 'string') {
            setFilters({ ...filters,
                appointment: option });
        }
    };

    const items = dateRanges.map((range) => ({
        label: range.title,
        value: range.key.toString(),
    }));

    const onDateChange = (date?: Date) => {
        setFilters({ ...filters,
            date: date,
            dateRange: date ? undefined : filters?.dateRange });
    };

    return (
        <FormProvider>
            <FilterRow formFilter>
                <GMAutocomplete
                    id={Localization.SearchName}
                    title={Localization.SearchName}
                    label={Localization.SearchName}
                    ariaLabel={Localization.SearchName}
                    labelClass={css.adminFormLabel}
                    getOptionLabel={getOptionLabel}
                    filterOptions={filterOptions}
                    renderOption={renderOption}
                    onOptionSelected={onOptionSelected}
                    options={results ?? []}
                    fullWidth
                    shiftAfterSpace
                    hideKeyboardOnClickOut
                />
                <DatePicker
                    name={nameof<FilterValues>('date')}
                    value={filters?.date}
                    onChange={onDateChange}
                    label={Localization.Date}
                    labelClass={css.adminFormLabel}
                />
                <GMSelect
                    id={Localization.DateRange}
                    name={nameof<FilterValues>('dateRange')}
                    label={Localization.DateRange}
                    value={filters?.dateRange?.key.toString() ?? ''}
                    items={items}
                    onChange={(value) => {
                        const dateRange = dateRanges.find((range) => range.key.toString() === value);
                        if (dateRange && dateRange.key !== DateRangeKey.none) {
                            setFilters({ ...filters,
                                dateRange,
                                date: undefined });
                        }
                        else {
                            setFilters({ ...filters,
                                dateRange: undefined });
                        }
                    }}
                    isAdminSelect
                    clearable
                />
            </FilterRow>
            <FilterRow>
                <FilterButtons>
                    {includeFilters.includes(AppointmentType.none) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.none,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.none]}
                            selected={!filters?.type}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.ShowAll) as string}
                        >
                            {Localization.ShowAll}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.priority) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.priority,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.priority]}
                            selected={filters?.type === AppointmentType.priority}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.Priority) as string}
                        >
                            {Localization.Priority}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.checkedIn) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.checkedIn,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.checkedIn]}
                            selected={filters?.type === AppointmentType.checkedIn}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.CheckedIn) as string}
                        >
                            {Localization.CheckedIn}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.created) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.created,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.created]}
                            selected={filters?.type === AppointmentType.created}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.ManuallyCreated) as string}
                        >
                            {Localization.ManuallyCreated}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.dmsAppointment) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.dmsAppointment,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.dmsAppointment]}
                            selected={filters?.type === AppointmentType.dmsAppointment}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.DMSAppointments) as string}
                        >
                            {Localization.DMSAppointments}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.keyDroppedOff) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.keyDroppedOff,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.keyDroppedOff]}
                            selected={filters?.type === AppointmentType.keyDroppedOff}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.DropOffs) as string}
                        >
                            {Localization.DropOffs}
                        </FilterButton>
                    )}
                    {includeFilters.includes(AppointmentType.inKeyBin) && (
                        <FilterButton
                            onClick={() => setFilters({ ...filters,
                                type: AppointmentType.inKeyBin,
                                appointment: undefined })}
                            color={AppointmentColors[AppointmentType.inKeyBin]}
                            selected={filters?.type === AppointmentType.inKeyBin}
                            ariaLabel={Localization.formatString(Localization.SetFilterTo, Localization.ReadyForPickUp) as string}
                        >
                            {Localization.ReadyForPickUp}
                        </FilterButton>
                    )}
                </FilterButtons>
            </FilterRow>
        </FormProvider>
    );
});
